// src/lakehouse/ui/admin/src/features/datasets/lib/validateFilterValue.ts
//
// Pure, total validation of Explore filter values. DuckDB already coerces a
// string param to the column type correctly (param -> column), so this is NOT
// about correctness — it pre-empts the unfriendly DuckDB ConversionException a
// malformed value would otherwise raise, surfacing a per-field message instead.
//
// Conservative by design: the patterns are deliberately permissive so a value
// DuckDB would accept is never rejected (a false-negative would block a valid
// query). String/other columns are never blocked.
import { classify } from './operatorsForType';
import type { FilterOperator, QueryFilter } from '../types/query.types';

const NO_VALUE: FilterOperator[] = ['is null', 'is not null'];
const MULTI: FilterOperator[] = ['in', 'not in'];

// sign, decimal, exponent; arbitrary digit length (validates shape, never converts,
// so HUGEINT strings are checked without JS number-precision loss).
const NUMERIC_RE = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;
// #657: DuckDB accepts inf/infinity/nan (optional sign, case-insensitive) for FLOAT/DOUBLE
// columns. classify() has a single 'numeric' kind, so these are also accepted for integer
// columns — a tolerated false-positive (backend friendly-fallback), never a false-negative.
const SPECIAL_FLOAT_RE = /^[+-]?(inf(inity)?|nan)$/i;
// permissive ISO date / datetime (1-2 digit month/day/hour to avoid false negatives)
const DATETIME_RE =
  /^\d{4}-\d{1,2}-\d{1,2}([ T]\d{1,2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;
const TIME_RE = /^\d{1,2}:\d{2}(:\d{2})?$/;
const BOOL_LITERALS = new Set(['true', 'false', 't', 'f', '1', '0', 'yes', 'no', 'on', 'off']);

type Kind = ReturnType<typeof classify>;

function scalarError(raw: unknown, kind: Kind): string | null {
  const v = (typeof raw === 'string' ? raw : '').trim(); // nosemgrep: rules.lgpl.nodejs_scan.javascript-dos-rule-regex_dos  -- validator uses anchored, bounded regexes over short trimmed filter input
  switch (kind) {
    case 'numeric':
      return NUMERIC_RE.test(v) || SPECIAL_FLOAT_RE.test(v) ? null : 'Enter a number';
    case 'temporal':
      return DATETIME_RE.test(v) || TIME_RE.test(v) ? null : 'Enter a date (e.g. 2024-01-31)';
    case 'boolean':
      return BOOL_LITERALS.has(v.toLowerCase()) ? null : 'Enter true or false';
    default:
      return null; // string / other — any value is acceptable
  }
}

export function validateFilterValue(filter: QueryFilter, colType: string): string | null {
  const { operator, value } = filter;
  if (NO_VALUE.includes(operator)) return null;

  // An empty IN/NOT IN list is invalid regardless of column type.
  if (MULTI.includes(operator)) {
    const arr = Array.isArray(value) ? value : [];
    if (arr.length === 0) return 'Enter at least one value';
  }

  const kind = classify(colType);
  if (kind === 'string' || kind === 'other') return null; // free-text columns: never block

  if (MULTI.includes(operator)) {
    for (const el of value as unknown[]) {
      const err = scalarError(el, kind);
      if (err) return err;
    }
    return null;
  }

  if (operator === 'between') {
    const arr = Array.isArray(value) ? value : ['', ''];
    return scalarError(arr[0], kind) ?? scalarError(arr[1], kind);
  }

  return scalarError(value, kind);
}

export function validateFilters(
  filters: QueryFilter[],
  columns: { name: string; type: string }[],
): (string | null)[] {
  return filters.map((f) =>
    validateFilterValue(f, columns.find((c) => c.name === f.column)?.type ?? ''),
  );
}
